import styled from 'styled-components';

import { BRAND_DEPLOYMENT_BOUNCE, BRAND_DEPLOYMENT_STRIP, MOBILE, REDUCED_MOTION } from '../../../../../theme/tokens';

/**
 * The horizontal scroll strip — every deploy step is a card in one long, edge-to-edge
 * row you scroll through (the Shopify brand-marquee model that replaced the fanned
 * stack). Full-bleed (negative inline margin cancels the Root's 24px padding) so cards
 * reach the panel border and clip there; the native scrollbar is hidden (chevrons +
 * drag + wheel drive it). Above the frosted glass (z-index 2).
 *
 * `overflow-x: auto` forces `overflow-y` to clip, so the fixed height keeps enough
 * vertical slack (STAGE_H > card height) for the per-card tilt + the centred card's
 * lift + scale to show without cropping.
 */
export const Strip = styled.div`
  position: relative;
  z-index: 2;
  width: calc(100% + 48px);
  margin-inline: -24px;
  height: ${BRAND_DEPLOYMENT_BOUNCE.STAGE_H}px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  /* No native CSS scroll-snap: momentum would overshoot a snap point and get yanked back
     (a bounce). useStripScroll snaps in JS instead — one monotonic ease to the nearest card
     centre once the scroll settles. scroll-behavior stays 'auto' so a drag's direct scrollLeft
     writes track 1:1; programmatic scrolls pass their own 'smooth'/'instant' behavior. */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* legacy Edge */
  cursor: grab;
  touch-action: pan-x;
  /* Grab-to-scroll: a drag must never start a text selection across the cards. */
  user-select: none;
  -webkit-user-select: none;

  &::-webkit-scrollbar {
    display: none; /* Chrome / Safari */
  }

  &:active {
    cursor: grabbing;
  }

  ${MOBILE} {
    width: calc(100% + 32px);
    margin-inline: -16px;
    height: ${BRAND_DEPLOYMENT_BOUNCE.STAGE_H_MOBILE}px;
  }
`;

/**
 * One card's placement in the strip: a fixed-size flow item that owns only its size and the
 * slight overlap (negative left margin). Its transform + z-index are written imperatively per
 * scroll frame by {@link useStripChoreography} (gap push, organic rotation, centre lift +
 * scale), NOT via React props — so it deliberately carries NO CSS transform transition: the
 * smoothness is the native scroll itself, and a transition would only lag/smear the per-frame
 * updates. The card surface (CardShell) inside just fills it.
 *
 * The first + last cards carry a leading / trailing margin of half the free width, so once
 * the strip is scrolled (or auto-follows) to either extreme, that end card lands CENTRED
 * rather than pinned to the panel edge — a symmetric centred carousel. Middle cards still
 * bleed edge-to-edge as they scroll past.
 */
export const Slot = styled.div`
  flex: 0 0 auto;
  position: relative;
  width: ${BRAND_DEPLOYMENT_BOUNCE.CARD_W}px;
  aspect-ratio: 3 / 4;
  margin-left: -${BRAND_DEPLOYMENT_STRIP.OVERLAP}px;
  /* Origin sits a little below the card's middle: the centred card's scale-up + lift grow it
     mostly downward-anchored (a grounded feel) while keeping its top clear of the clip edge. */
  transform-origin: 50% 82%;
  will-change: transform;

  &:first-child {
    margin-left: max(0px, calc(50% - ${BRAND_DEPLOYMENT_BOUNCE.CARD_W / 2}px));
  }

  &:last-child {
    margin-right: max(0px, calc(50% - ${BRAND_DEPLOYMENT_BOUNCE.CARD_W / 2}px));
  }

  ${MOBILE} {
    width: ${BRAND_DEPLOYMENT_BOUNCE.CARD_W_MOBILE}px;
    margin-left: -${BRAND_DEPLOYMENT_STRIP.OVERLAP_MOBILE}px;

    &:first-child {
      margin-left: max(0px, calc(50% - ${BRAND_DEPLOYMENT_BOUNCE.CARD_W_MOBILE / 2}px));
    }

    &:last-child {
      margin-right: max(0px, calc(50% - ${BRAND_DEPLOYMENT_BOUNCE.CARD_W_MOBILE / 2}px));
    }
  }

  ${REDUCED_MOTION} {
    will-change: auto;
  }
`;
